import { FEATURE_KEYS, type UpdateNotificationSettingsInput } from "@school-sales-ai/shared";
import { PageHeader } from "@/components/PageHeader";
import { Switch } from "@/components/ui/switch";
import { useHasClientFeature } from "@/hooks/useClientFeatures";
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
} from "@/hooks/useNotifications";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** One switch with its explanation — the `LeadAssignmentSettings` row. */
function SettingToggle({
  id,
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-[13px] font-medium text-ink-900">
          {label}
        </label>
        <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">{help}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={label}
      />
    </div>
  );
}

/**
 * Admin → «Notificaciones» (plan feature `web_notifications`).
 *
 * Every switch saves the moment it is flipped, like the lead-assignment policy:
 * each one decides what other people are told, and an unsaved pending state for
 * that would be a trap. An event only appears when the client can produce it —
 * a school with no cart is never asked about confirmed orders.
 */
export function Notificaciones(): React.JSX.Element {
  const { data: settings, isPending, isError } = useNotificationSettings();
  const update = useUpdateNotificationSettings();
  const hasOrders = useHasClientFeature(FEATURE_KEYS.orders);
  const hasQuotes = useHasClientFeature(FEATURE_KEYS.quotes);

  function set(patch: UpdateNotificationSettingsInput): void {
    update.mutate(patch);
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageHeader title="Notificaciones" />
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          <p className="text-[13px] leading-relaxed text-ink-500">
            Avisos dentro del CRM (la campana de arriba a la derecha) y en el navegador,
            para cada persona que los active desde la campana. Llegan al miembro del
            equipo que tiene asignado el prospecto, o a todos los miembros si nadie lo
            tiene asignado.
          </p>

          {isPending ? (
            <p className="text-[13px] text-ink-500">Cargando…</p>
          ) : isError || !settings ? (
            <p className="text-[13px] text-red-500">No se pudo cargar la configuración.</p>
          ) : (
            <>
              <div className="flex flex-col gap-4 rounded-lg border border-ink-100 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                  Eventos
                </p>
                {hasOrders && (
                  <SettingToggle
                    id="notify-order-confirmed"
                    label="Pedido confirmado"
                    help="Cuando el agente confirma un pedido de un cliente."
                    checked={settings.orderConfirmed}
                    disabled={update.isPending}
                    onChange={(checked) => set({ orderConfirmed: checked })}
                  />
                )}
                {hasQuotes && (
                  <SettingToggle
                    id="notify-quote-requested"
                    label="Cotización creada"
                    help="Cuando un cliente envía una nueva solicitud de cotización."
                    checked={settings.quoteRequested}
                    disabled={update.isPending}
                    onChange={(checked) => set({ quoteRequested: checked })}
                  />
                )}
                <SettingToggle
                  id="notify-lead-handoff"
                  label="Lead transferido a humano"
                  help="Cuando el agente pasa un prospecto al equipo, o el prospecto pide hablar con una persona."
                  checked={settings.leadHandoff}
                  disabled={update.isPending}
                  onChange={(checked) => set({ leadHandoff: checked })}
                />
              </div>

              <div className="rounded-lg border border-ink-100 bg-white p-4">
                <SettingToggle
                  id="notify-admins"
                  label="Administrador recibe notificaciones"
                  help="Encendido: los administradores reciben todas las notificaciones. Apagado: un administrador solo las recibe de los prospectos que tiene asignados."
                  checked={settings.notifyAdmins}
                  disabled={update.isPending}
                  onChange={(checked) => set({ notifyAdmins: checked })}
                />
              </div>

              {settings.updatedAt && (
                <p className="text-xs text-ink-500">
                  Último cambio
                  {settings.updatedByName ? ` por ${settings.updatedByName}` : ""} el{" "}
                  {formatDate(settings.updatedAt)}.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
